import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FaqAccordion from "@/components/feature/FaqAccordion";
import SubmissionForm from "./components/SubmissionForm";
import { categories, faqs, siteMeta, submissionSteps } from "@/mocks/leads";
import { useSeo, BASE_URL } from "@/hooks/useSeo";
import { useScrollReveal } from "@/hooks/useScrollReveal";

const sections = [
  { id: "scope", label: "可投范围" },
  { id: "flow", label: "投稿流程" },
  { id: "protect", label: "来源保护" },
  { id: "submit", label: "在线投稿" },
  { id: "guide-faq", label: "常见问题" },
];

const protections = [
  {
    title: "匿名是默认状态",
    desc: "投稿不需要真实姓名。你提供的联系方式仅用于核实期间的沟通，不会出现在任何公开内容中。",
    icon: "ri-user-forbid-line",
  },
  {
    title: "材料最小化访问",
    desc: "原始材料按线索分库存储，仅有当条线索的核实编辑拥有访问权限，且操作留痕。",
    icon: "ri-folder-lock-line",
  },
  {
    title: "呈现范围共同确认",
    desc: "涉及你的身份、位置或可识别信息的细节，都会在发布前逐条与你确认，你可以要求模糊处理。",
    icon: "ri-equalizer-line",
  },
  {
    title: "随时可以喊停",
    desc: "在内容发布之前，你可以在任何阶段要求终止核实流程并撤回材料，不需要提供理由。",
    icon: "ri-stop-circle-line",
  },
];

export default function Guide() {
  useScrollReveal();

  useSeo({
    title: "投稿指南 - 蝌蚪爆料线索征集｜投稿入口与来源保护说明",
    description:
      "蝌蚪爆料投稿指南：说明可投稿的线索范围、四步核实流程、来源与隐私保护机制，并提供在线投稿入口。欢迎提供真实素材，投稿人默认匿名，48 小时内响应。",
    keywords: "蝌蚪爆料投稿,蝌蚪爆料征集,线索投稿入口,来源保护,匿名投稿",
    path: "/guide",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "蝌蚪爆料投稿指南",
        url: `${BASE_URL}/guide`,
        inLanguage: "zh-CN",
        description: "线索投稿范围、核实流程与来源保护机制的完整说明。",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: BASE_URL },
          { "@type": "ListItem", position: 2, name: "投稿指南", item: `${BASE_URL}/guide` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  });

  return (
    <>
      <section className="w-full border-b border-background-200 bg-background-100 py-10 md:py-14">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "投稿指南" }]} />
          <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-8">
              <h1 className="font-heading text-3xl font-bold text-foreground-950 md:text-5xl">
                投稿指南
              </h1>
              <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
                我们相信，很多重要的信息就在亲历者手上。这份指南会告诉你什么样的线索值得投、
                投递之后会发生什么，以及我们如何保护每一位投稿人。
              </p>
            </div>
            <div className="lg:col-span-4">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <p className="text-xs text-foreground-500">投稿咨询</p>
                <p className="mt-1 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-phone-line text-primary-500" aria-hidden="true" />
                  {siteMeta.hotline}
                </p>
                <p className="mt-1 flex items-center gap-2 text-sm text-foreground-600">
                  <i className="ri-mail-line text-primary-500" aria-hidden="true" />
                  {siteMeta.email}
                </p>
              </div>
            </div>
          </div>

          <nav aria-label="本页目录" className="mt-8 flex flex-wrap gap-2">
            {sections.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="rounded-full border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
              >
                {s.label}
              </a>
            ))}
          </nav>
        </div>
      </section>

      <section id="scope" className="w-full bg-background-50 py-14 md:py-16">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <div className="reveal max-w-2xl">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              这些方向的线索，我们最需要
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              只要与公共利益相关、且你有真实的第一手了解，就值得投递。以下栏目是我们长期重点跟进的领域。
            </p>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {categories.map((c) => (
              <div
                key={c.id}
                className="reveal flex items-start gap-4 rounded-lg border border-background-200 bg-background-100 p-5"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className={`${c.icon} text-xl`} aria-hidden="true" />
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">{c.name}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-foreground-600">{c.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="flow" className="w-full bg-background-100 py-14 md:py-16">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <div className="reveal max-w-2xl">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              投递之后会发生什么
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              从你按下提交的那一刻起，这条线索会进入固定的核实流程。每一步的进展，我们都会与你同步。
            </p>
          </div>
          <ol className="mt-8 grid grid-cols-1 gap-0 md:grid-cols-4">
            {submissionSteps.map((s, i) => (
              <li key={s.step} className="reveal relative flex flex-col border-t-2 border-background-300 pt-6 md:pr-6">
                <span className="absolute -top-[9px] left-0 flex h-4 w-4 items-center justify-center rounded-full bg-primary-500" />
                <span className="font-heading text-2xl font-bold text-primary-600">{s.step}</span>
                <h3 className="mt-3 font-heading text-lg font-bold text-foreground-950">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{s.desc}</p>
                {i < submissionSteps.length - 1 && (
                  <i className="ri-arrow-right-line mt-4 hidden text-foreground-300 md:block md:absolute md:right-1 md:top-14" aria-hidden="true" />
                )}
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section id="protect" className="w-full bg-foreground-950 py-14 md:py-16">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <div className="reveal max-w-2xl">
            <h2 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
              我们对来源保护的具体做法
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-background-300 md:text-base">
              保护投稿人不是一句口号，而是一整套贯穿流程的操作规范。以下四点，是我们对自己最严格的要求。
            </p>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {protections.map((p) => (
              <div
                key={p.title}
                className="reveal flex items-start gap-4 rounded-lg border border-background-50/12 bg-foreground-900/60 p-5"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-accent-500/15 text-accent-400">
                  <i className={`${p.icon} text-xl`} aria-hidden="true" />
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-background-50">{p.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-background-400">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="submit" className="w-full bg-background-50 py-14 md:py-16">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="reveal lg:col-span-4">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                在线投稿入口
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                填写下面的表单即可完成投稿。不需要写得很完整，把你确定的部分先写下来，
                剩下的细节核实编辑会和你一起补充。
              </p>
              <ul className="mt-6 space-y-3 text-sm text-foreground-700">
                <li className="flex gap-2.5">
                  <i className="ri-check-line mt-0.5 text-primary-600" aria-hidden="true" />
                  投稿不需要注册账号
                </li>
                <li className="flex gap-2.5">
                  <i className="ri-check-line mt-0.5 text-primary-600" aria-hidden="true" />
                  48 小时内会收到编辑联系
                </li>
                <li className="flex gap-2.5">
                  <i className="ri-check-line mt-0.5 text-primary-600" aria-hidden="true" />
                  未采用的线索同样会收到说明
                </li>
              </ul>
              <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5">
                <p className="font-heading text-sm font-semibold text-foreground-950">
                  不方便填写表单？
                </p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  可以直接发送邮件到
                  <span className="mx-1 font-medium text-primary-700">{siteMeta.email}</span>
                  ，或添加微信
                  <span className="mx-1 font-medium text-primary-700">{siteMeta.wechat}</span>
                  说明来意。
                </p>
              </div>
            </div>
            <div className="lg:col-span-8">
              <SubmissionForm />
            </div>
          </div>
        </div>
      </section>

      <section id="guide-faq" className="w-full bg-background-100 py-14 md:py-16">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="reveal lg:col-span-4">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                投稿常见问题
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                这些问题来自投稿人最真实的顾虑。如果还有疑问，欢迎在投稿表单里一并提出。
              </p>
              <Link
                to="/about"
                className="mt-6 inline-flex items-center gap-2 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
              >
                了解我们的内容原则
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            </div>
            <div className="reveal lg:col-span-8">
              <FaqAccordion items={faqs} />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}